import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentCTA } from "@/components/sections/AppointmentCTA";
import { Reveal } from "@/components/common/Reveal";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export default function DoctorProfile() {
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDoctor(null); setNotFound(false);
    api.get(`/doctors/${slug}`).then((r) => setDoctor(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center"><h1 className="font-display text-3xl">Doctor not found</h1><Link to="/doctors" className="mt-4 inline-block text-[var(--cdt-primary)] font-semibold">Back to doctors</Link></div>;
  if (!doctor) return <div className="max-w-6xl mx-auto px-6 py-24"><Skeleton className="h-96 w-full rounded-[var(--cdt-radius-lg)]" /></div>;

  const jsonLd = { "@context": "https://schema.org", "@type": "Physician", name: doctor.name, jobTitle: doctor.title, medicalSpecialty: "Dentistry" };

  return (
    <>
      <Seo title={doctor.name} description={doctor.philosophy} path={`/doctors/${slug}`} image={doctor.image} jsonLd={jsonLd} type="profile" />
      <section className="pt-12 lg:pt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[360px_1fr] gap-10 items-start">
          <Reveal>
            <div className="rounded-[var(--cdt-radius-lg)] overflow-hidden shadow-[var(--cdt-shadow-md)]"><img src={doctor.image} alt={doctor.name} className="w-full h-[420px] object-cover" /></div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[var(--cdt-accent)]">{doctor.experience} experience</span>
            <h1 className="font-display text-4xl mt-2 text-[var(--cdt-text)]">{doctor.name}</h1>
            <p className="text-[var(--cdt-primary)] font-medium mt-1">{doctor.title}</p>
            <div className="flex flex-wrap gap-1.5 mt-4">{doctor.qualifications.map((q) => <Badge key={q} variant="secondary" className="bg-[var(--cdt-surface-2)] text-[var(--cdt-text)]">{q}</Badge>)}</div>
            <p className="mt-6 text-[var(--cdt-muted)] leading-8">{doctor.bio}</p>
            <div className="mt-6 rounded-[var(--cdt-radius-md)] bg-[var(--cdt-surface-2)] border border-[color:var(--cdt-border)] p-5">
              <p className="text-sm italic text-[var(--cdt-text)] leading-7">“{doctor.philosophy}”</p>
            </div>
            <h3 className="font-display text-xl mt-8 text-[var(--cdt-text)]">Areas of focus</h3>
            <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {doctor.areas_of_focus?.map((a) => (
                <div key={a} className="flex items-center gap-2.5 text-sm text-[var(--cdt-text)]"><CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 text-[var(--cdt-primary)] flex-shrink-0" /> {a}</div>
              ))}
            </div>
            <Link to="/appointment" data-testid="doctor-appointment-button" className="mt-8 inline-flex items-center gap-2 h-11 px-5 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold hover:bg-[var(--cdt-primary-2)] transition-colors">Book with {doctor.name.split(" ")[0]} {doctor.name.split(" ")[1]} <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
      <div className="mt-16"><AppointmentCTA /></div>
    </>
  );
}
